import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Camera, Car, ExternalLink, FileText, Image as ImageIcon, Mail, MessageCircle, Monitor, ScanLine, Search, Trash2, X } from "lucide-react";
import { API } from "../lib/apiClient";
import { waTarget } from "../lib/wa";
import { Empty } from "./common";
import { IdOcrCompare } from "./IdOcrCompare";

export const DOC_CATEGORIES = [["kimlik", "Kimlik"], ["police", "Poliçe"], ["ruhsat", "Ruhsat"], ["diger", "Diğer"]];
const CAT = Object.fromEntries(DOC_CATEGORIES);
const SOURCES = [
  ["gallery", "Galeriden seç", ImageIcon, "image/*", undefined],
  ["computer", "Bilgisayardan aktar", Monitor, "application/pdf,image/*", undefined],
  ["camera", "Kamera ile çek", Camera, "image/*", "environment"],
];
export const compactPlate = (s) => String(s || "").toLocaleUpperCase("tr-TR").replace(/İ/g, "I").replace(/[^A-Z0-9]/g, "");
const trLow = (s) => String(s || "").toLocaleLowerCase("tr-TR");
const fileUrl = (id) => `${API}/customer-documents/${id}/file?token=${encodeURIComponent(localStorage.getItem("sigortapro-token") || "")}`;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const OCR_TYPES = /^image\/(jpe?g|png|webp)$/i;

function OcrResult({ ocr, customer, vehicles, onVehicleSaved }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const existing = vehicles.find((v) => compactPlate(v.plate) === compactPlate(ocr.plate));
  const save = async () => {
    setBusy(true); setMsg("");
    const fields = Object.fromEntries(["plate", "brand", "model", "year", "ruhsatSeriNo"].filter((k) => ocr[k]).map((k) => [k, ocr[k]]));
    try {
      const { data } = existing
        ? await axios.put(`${API}/vehicles/${existing.id}`, { ...existing, ...fields, customer: existing.customer || customer.name })
        : await axios.post(`${API}/vehicles`, { ...fields, customer: customer.name });
      onVehicleSaved?.(data);
      setMsg(existing ? "Araç güncellendi" : "Araç kaydedildi");
    } catch (e) { setMsg(e?.response?.data?.detail || "Araç kaydedilemedi"); } finally { setBusy(false); }
  };
  const rows = [["Plaka", ocr.plate], ["Seri no", ocr.ruhsatSeriNo], ["Marka / Model", [ocr.brand, ocr.model].filter(Boolean).join(" ")], ["Model yılı", ocr.year], ["Ruhsat sahibi", ocr.owner]];
  return (
    <div className="doc-ocr" data-testid="doc-ocr-result">
      <div className="doc-ocr-grid">
        {rows.map(([l, v]) => <div key={l}><small>{l}</small><strong data-testid={`doc-ocr-${l}`}>{v || "—"}</strong></div>)}
      </div>
      {ocr.plate && (
        <div className="doc-ocr-actions">
          <button type="button" className="outline-button" onClick={save} disabled={busy} data-testid="doc-ocr-save-vehicle"><Car size={14} /> {busy ? "Kaydediliyor…" : existing ? "Araçlarda güncelle" : "Araçlara kaydet"}</button>
          {msg && <small data-testid="doc-ocr-vehicle-msg">{msg}</small>}
        </div>
      )}
    </div>
  );
}

function UploadForm({ customer, preset, onUploaded, vehicles, onVehicleSaved, onCustomerUpdated }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("kimlik");
  const [name, setName] = useState("");
  const [plate, setPlate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ocr, setOcr] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!preset) return;
    setCategory(preset.category); setPlate(preset.plate || "");
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [preset]);
  useEffect(() => {
    const endpoint = { ruhsat: "vehicles/ocr-ruhsat", kimlik: "customers/ocr-kimlik" }[category];
    if (!file || !endpoint || !OCR_TYPES.test(file.type) || ocr?.kind === category || ocrBusy) return;
    setOcrBusy(true);
    const fd = new FormData();
    fd.append("file", file, file.name);
    axios.post(`${API}/${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => {
        setOcr({ ...data, kind: category });
        if (category === "ruhsat" && data.plate) { setPlate(compactPlate(data.plate)); setName((n) => n || `Ruhsat ${data.plate}${data.brand ? ` · ${data.brand} ${data.model || ""}`.trimEnd() : ""}`); }
        if (category === "kimlik") setName((n) => n || `Kimlik${data.name ? ` · ${data.name}` : ""}`);
      })
      .catch(() => setOcr({ failed: true, kind: category }))
      .finally(() => setOcrBusy(false));
  }, [file, category, ocrBusy]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (f) => {
    setErr(""); setOcr(null);
    if (!f) return;
    if (!/^(application\/pdf|image\/)/i.test(f.type)) { setErr("Sadece PDF veya resim yüklenebilir"); return; }
    if (f.size > 10 * 1024 * 1024) { setErr("Dosya en fazla 10MB olabilir"); return; }
    setFile(f);
  };
  const reset = () => { setFile(null); setName(""); setPlate(""); setOcr(null); };
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name); fd.append("category", category); fd.append("name", name); fd.append("plate", plate);
      const { data } = await axios.post(`${API}/customers/${customer.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data); reset();
    } catch (e) { setErr(e?.response?.data?.detail || "Yükleme başarısız"); } finally { setBusy(false); }
  };
  return (
    <div className="doc-upload" data-testid="doc-upload" ref={boxRef}>
      <div className="doc-cats">
        {DOC_CATEGORIES.map(([k, l]) => <button key={k} type="button" className={category === k ? "active" : ""} onClick={() => setCategory(k)} data-testid={`doc-cat-${k}`}>{l}</button>)}
        {(category === "ruhsat" || category === "kimlik") && <span className="doc-ocr-hint"><ScanLine size={13} /> {category === "kimlik" ? "Kimlik" : "Ruhsat"} fotoğrafı otomatik okunur</span>}
      </div>
      {!file ? (
        <div className="doc-sources">
          {SOURCES.map(([k, l, Icon, accept, capture]) => (
            <label key={k} className="doc-source" data-testid={`doc-source-${k}`}>
              <Icon size={18} /><span>{l}</span>
              <input type="file" accept={accept} capture={capture} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`doc-input-${k}`} />
            </label>
          ))}
        </div>
      ) : (
        <div className="doc-pending" data-testid="doc-pending">
          <div className="doc-pending-file"><FileText size={16} /><span>{file.name}</span><em>{kb(file.size)}</em><button type="button" onClick={reset} title="Vazgeç" data-testid="doc-pending-cancel"><X size={14} /></button></div>
          {ocrBusy && <div className="doc-ocr-loading" data-testid="doc-ocr-loading"><ScanLine size={14} /> {category === "kimlik" ? "Kimlik" : "Ruhsat"} okunuyor…</div>}
          {ocr?.failed && ocr.kind === category && <small className="doc-error" data-testid="doc-ocr-failed">{category === "kimlik" ? "Kimlik" : "Ruhsat"} okunamadı, bilgileri elle girebilirsiniz.</small>}
          {ocr && !ocr.failed && ocr.kind === "ruhsat" && category === "ruhsat" && <OcrResult ocr={ocr} customer={customer} vehicles={vehicles} onVehicleSaved={onVehicleSaved} />}
          {ocr && !ocr.failed && ocr.kind === "kimlik" && category === "kimlik" && (
            <IdOcrCompare ocr={ocr} current={customer} testPrefix="doc-id-ocr" onApply={async (fields) => {
              const { data } = await axios.put(`${API}/customers/${customer.id}`, { name: customer.name, ...fields });
              onCustomerUpdated?.(data);
            }} />
          )}
          <div className="doc-pending-fields">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Belge adı (örn. Ehliyet ön yüz)" data-testid="doc-name-input" />
            <input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} maxLength={14} placeholder="Plaka (isteğe bağlı)" data-testid="doc-plate-input" />
            <button type="button" className="primary-button" onClick={save} disabled={busy || ocrBusy} data-testid="doc-save-button">{busy ? "Yükleniyor…" : `${CAT[category]} olarak kaydet`}</button>
          </div>
        </div>
      )}
      {err && <small className="doc-error" data-testid="doc-error">{err}</small>}
    </div>
  );
}

function ShareButtons({ doc, customer }) {
  const [busy, setBusy] = useState("");
  const whatsapp = async () => {
    const w = window.open("", "_blank");
    setBusy("wa");
    try {
      const { data } = await axios.post(`${API}/customer-documents/${doc.id}/share-link`);
      const link = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      const text = `Merhaba ${customer.name}, ${doc.name || CAT[doc.category]} belgeniz: ${link} (bağlantı 7 gün geçerlidir)`;
      const target = waTarget(customer.phone);
      const url = `https://wa.me/${target}?text=${encodeURIComponent(text)}`;
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { w?.close(); window.alert(e?.response?.data?.detail || "Bağlantı oluşturulamadı"); } finally { setBusy(""); }
  };
  const email = async () => {
    const to = window.prompt("Belge hangi adrese gönderilsin?", customer.email || "");
    if (!to) return;
    setBusy("mail");
    try {
      await axios.post(`${API}/customer-documents/${doc.id}/email`, { to: to.trim() });
      window.alert(`Belge ${to.trim()} adresine gönderildi`);
    } catch (e) { window.alert(e?.response?.data?.detail || "E-posta gönderilemedi"); } finally { setBusy(""); }
  };
  return (
    <>
      <button type="button" className="row-action share wa" onClick={whatsapp} disabled={!!busy} title="WhatsApp ile gönder (7 gün geçerli link)" data-testid={`doc-share-wa-${doc.id}`}><MessageCircle size={15} /></button>
      <button type="button" className="row-action share" onClick={email} disabled={!!busy} title="E-posta ile gönder (dosya ekte)" data-testid={`doc-share-mail-${doc.id}`}><Mail size={15} /></button>
    </>
  );
}

export function DocumentArchive({ customer, docs, setDocs, preset, vehicles = [], onVehicleSaved, onCustomerUpdated }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const shown = useMemo(() => {
    const term = trLow(q).replace(/\s+/g, " ").trim();
    const compact = compactPlate(term);
    return (docs || []).filter((d) => (!cat || d.category === cat) && (trLow(`${d.name} ${d.filename} ${CAT[d.category]}`).includes(term) || (compact && (compactPlate(d.plate).includes(compact) || compactPlate(d.name).includes(compact)))));
  }, [docs, q, cat]);
  const remove = async (d) => {
    if (!window.confirm(`"${d.name || d.filename}" silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/customer-documents/${d.id}`);
      setDocs((list) => list.filter((x) => x.id !== d.id));
    } catch (e) { window.alert(e?.response?.data?.detail || "Belge silinemedi"); }
  };
  return (
    <div className="doc-archive" data-testid="document-archive">
      <UploadForm customer={customer} preset={preset} vehicles={vehicles} onVehicleSaved={onVehicleSaved} onCustomerUpdated={onCustomerUpdated} onUploaded={(d) => setDocs((list) => [d, ...(list || [])])} />
      <div className="doc-toolbar">
        <div className="inline-search"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Belge adı, dosya adı veya plaka ara…" data-testid="doc-search-input" /></div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="doc-filter-category">
          <option value="">Tüm belgeler</option>
          {DOC_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <span className="ctl-count" data-testid="doc-count">{shown.length} belge</span>
      </div>
      {docs === null ? <div className="ctl-count">Yükleniyor…</div> : shown.length === 0 ? <Empty text={docs.length ? "Aramaya uyan belge yok" : "Henüz belge eklenmedi"} /> : (
        <ul className="doc-list">
          {shown.map((d) => (
            <li key={d.id} data-testid={`doc-item-${d.id}`}>
              <a className="doc-thumb" href={fileUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-open-${d.id}`}>
                {d.contentType.startsWith("image/") && !/hei[cf]/.test(d.contentType) ? <img src={fileUrl(d.id)} alt="" loading="lazy" /> : <FileText size={20} />}
              </a>
              <div className="doc-info">
                <strong>{d.name || d.filename}</strong>
                <span><b className={`doc-cat-tag ${d.category}`}>{CAT[d.category]}</b>{d.plate && <span className="plate-chip">{d.plate}</span>}{d.name ? d.filename : ""}</span>
                <em data-testid={`doc-date-${d.id}`}>Eklenme: {new Date(d.createdAt).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })} · {kb(d.size)}</em>
              </div>
              <div className="doc-actions">
                <ShareButtons doc={d} customer={customer} />
                <a className="row-action" href={fileUrl(d.id)} target="_blank" rel="noreferrer" title="Aç / indir"><ExternalLink size={15} /></a>
                <button type="button" className="row-action" onClick={() => remove(d)} title="Sil" data-testid={`doc-delete-${d.id}`}><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
